import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Mail, Pencil, Eye, RotateCcw, Save, Loader2, CheckCircle, Code, Info
} from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

import API from '@/config/api';
// API imported from config;

const EmailTemplatesSettings = ({ getAuthHeaders }) => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [editDialog, setEditDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-templates`, getAuthHeaders());
      setTemplates(res.data);
    } catch (error) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (template) => {
    setEditingTemplate({
      ...template,
      subject: template.subject,
      body: template.body
    });
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/email-templates/${editingTemplate.id}`,
        {
          subject: editingTemplate.subject,
          body: editingTemplate.body,
          is_enabled: editingTemplate.is_enabled
        },
        getAuthHeaders()
      );
      toast.success('Template saved successfully');
      setEditDialog(false);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (templateId) => {
    if (!confirm('Reset this template to default? Your customizations will be lost.')) return;
    
    try {
      await axios.post(`${API}/email-templates/${templateId}/reset`, {}, getAuthHeaders());
      toast.success('Template reset to default');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  const handlePreview = async (template) => {
    try {
      const res = await axios.post(
        `${API}/email-templates/${template.id}/preview`,
        {},
        getAuthHeaders()
      );
      setPreviewHtml(res.data.html);
      setPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const toggleEnabled = async (template) => {
    try {
      await axios.put(
        `${API}/email-templates/${template.id}`,
        { is_enabled: !template.is_enabled },
        getAuthHeaders()
      );
      toast.success(`Template ${!template.is_enabled ? 'enabled' : 'disabled'}`);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Email Templates
          </h3>
          <p className="text-sm text-muted-foreground">
            Customize the emails sent to parents and staff
          </p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Template Variables</p>
            <p className="text-blue-700">
              Use <code className="bg-blue-100 px-1 rounded">{"{{variable_name}}"}</code> to insert dynamic content. 
              Available variables are shown when editing each template.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 [&>*]:min-w-0">
        {templates.map((template) => (
          <Card key={template.id} className={`${!template.is_enabled ? 'opacity-60' : ''}`}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium">{template.name}</h4>
                    {template.is_customized && (
                      <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
                        Customized
                      </Badge>
                    )}
                    {!template.is_enabled && (
                      <Badge variant="outline" className="text-xs text-red-600 border-red-300">
                        Disabled
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{template.description}</p>
                  <p className="text-xs text-muted-foreground mt-1 font-mono truncate">
                    Subject: {template.subject}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={template.is_enabled}
                    onCheckedChange={() => toggleEnabled(template)}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handlePreview(template)}
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditDialog(template)}
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {template.is_customized && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleReset(template.id)}
                      title="Reset to default"
                      className="text-amber-600 hover:text-amber-700"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Edit: {editingTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate?.description}
            </DialogDescription>
          </DialogHeader>
          
          {editingTemplate && (
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {/* Available Variables */}
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm font-medium mb-2 flex items-center gap-1">
                  <Code className="w-4 h-4" />
                  Available Variables:
                </p>
                <div className="flex flex-wrap gap-2">
                  {editingTemplate.variables?.map((v) => (
                    <code 
                      key={v} 
                      className="text-xs bg-white px-2 py-1 rounded border cursor-pointer hover:bg-blue-50"
                      onClick={() => {
                        navigator.clipboard.writeText(`{{${v}}}`);
                        toast.success(`Copied {{${v}}} to clipboard`);
                      }}
                    >
                      {`{{${v}}}`}
                    </code>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject">Email Subject</Label>
                <Input
                  id="subject"
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                  placeholder="Email subject line..."
                />
              </div>

              {/* Body */}
              <div className="space-y-2">
                <Label htmlFor="body">Email Body (HTML)</Label>
                <Textarea
                  id="body"
                  value={editingTemplate.body}
                  onChange={(e) => setEditingTemplate({...editingTemplate, body: e.target.value})}
                  placeholder="Email content..."
                  className="min-h-[300px] font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Use HTML tags for formatting. The content will be wrapped in the CareKids WI email template.
                </p>
              </div>

              {/* Enable/Disable */}
              <div className="flex items-center gap-3 pt-2 border-t">
                <Switch
                  id="enabled"
                  checked={editingTemplate.is_enabled}
                  onCheckedChange={(checked) => setEditingTemplate({...editingTemplate, is_enabled: checked})}
                />
                <Label htmlFor="enabled" className="cursor-pointer">
                  Enable this email template
                </Label>
              </div>
            </div>
          )}

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              Email Preview
            </DialogTitle>
            <DialogDescription>
              This is how the email will look with sample data
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[500px] border rounded-lg">
            <div 
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml || '', { USE_PROFILES: { html: true } }) }}
              className="p-2"
            />
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesSettings;
